/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Empty,
  Flex,
  Form,
  InputNumber,
  Popconfirm,
  Row,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
  Modal,
} from "antd";
import {
  ArrowLeftOutlined,
  PrinterOutlined,
  ArrowRightOutlined,
  DeleteOutlined,
  SaveFilled,
  FileTextOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { RiDeleteBin5Line } from "react-icons/ri";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { formatMoney } from "../../../utils/util";
import ProductionOrderService from "../../../service/ERP/ProductionOrder.service";
import MasterProcessService from "../../../service/ERP/MasterProcess.service";
import ModalProductOrderSelector from "../../../components/modal/ERP/pd/MyModal";
import ProcessStepsPanel from "./ProcessStepsPanel";
import {
  batchMaterialColumns,
  batchPlanningColumns,
  jobOrderRefColumns,
  masterProcessColumns,
  requirementColumns,
  totalMaterialColumns,
} from "./model";

/* ปุ่มแถวล่างของหน้า PD ใช้ขนาดเดียวกันทั้งหมด (กว้างพอข้อความยาวสุด "ห้องสารส่วนผสม") */
const ACTION_BTN = { minWidth: "9.5rem", height: 36, paddingInline: 12 };
/* ปุ่มพิมพ์: พื้นขาว ขอบเทา แยกจากปุ่มดำเนินการ (สีทึบ) */
const PRINT_BTN = { ...ACTION_BTN, background: "#fff", borderColor: "#d9d9d9", color: "#434343" };
const GROUP_BOX = { padding: "4px 8px", borderRadius: 8 };
const PRINT_GROUP = { ...GROUP_BOX, border: "1px dashed #d9d9d9", background: "#fafafa" };
const ACTION_GROUP = { ...GROUP_BOX, border: "1px solid #d6e4ff", background: "#f0f5ff" };
const GROUP_LABEL = { fontSize: 12, whiteSpace: "nowrap", marginInline: 4 };

const productionOrderService = ProductionOrderService();
const masterProcessService = MasterProcessService();
const gotoFrom = "/ERP/product-order";
const DEFAULT_PRODUCE_TYPE = "FG";
const DECIMAL_PRECISION = 4;
const STOCK_DECIMAL_PRECISION = 2;

const roundStockQty = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) {
    return 0;
  }

  return Number(numeric.toFixed(STOCK_DECIMAL_PRECISION));
};

const getIssueItemKey = (item) =>
  `${item?.item_group || item?.typename || "item"}:${item?.mat_code || ""}`;

const buildAutoLotAllocationRows = (item) => {
  let remainingQty = roundStockQty(item?.required_qty || 0);

  return (item?.lot_options || []).map((lot) => {
    const availableQty = roundStockQty(lot?.available_qty || 0);
    const issueQty = roundStockQty(Math.min(remainingQty, availableQty));
    remainingQty = roundStockQty(remainingQty - issueQty);

    return {
      ...lot,
      available_qty: availableQty,
      issue_qty: issueQty,
    };
  });
};

const buildAutoLotAllocationMap = (items = []) =>
  items.reduce((result, item) => {
    result[getIssueItemKey(item)] = buildAutoLotAllocationRows(item);
    return result;
  }, {});

const formatLotDate = (value) =>
  value ? dayjs(value).format("DD/MM/YYYY") : "-";

const getBaseQty = (record) => {
  if (!record) {
    return 0;
  }

  return (
    Number(record?.total_unit || 0) ||
    Number(record?.carton || 0) * Number(record?.unit_cs || 0)
  );
};

const getDefaultQty = (record) => {
  if (!record) {
    return 0;
  }

  if (record?.qty !== undefined && record?.qty !== null) {
    return Number(record?.qty || 0);
  }

  return getBaseQty(record);
};

const roundQty = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) {
    return 0;
  }

  return Number(numeric.toFixed(DECIMAL_PRECISION));
};

const getNetWeightPerUnit = (record) => {
  const netWeight = Number(record?.net_weight || 0);

  return roundQty(netWeight);
};

const getSauceWeight = (record, totalQty = getDefaultQty(record)) =>
  roundQty(
    (Math.max(roundQty(totalQty), 0) * getNetWeightPerUnit(record)) / 1000,
  );

const getPreparedSauceWeight = (record, totalQty = getDefaultQty(record)) => {
  const sauceWeight = getSauceWeight(record, totalQty);
  const lost = Number(record?.lost || 0);
  const lostFactor = 1 - lost / 100;
  return roundQty(lostFactor > 0 ? sauceWeight / lostFactor : sauceWeight);
};

const getTotalPreparedSauceWeight = (
  record,
  totalQty = getDefaultQty(record),
) => getPreparedSauceWeight(record, totalQty);

const getTotalSauceWeight = (record, totalQty = getDefaultQty(record)) =>
  getSauceWeight(record, totalQty);

const sanitizeSauceBatchWeight = (value, fallback = 0) => {
  const numeric = Number(value || 0);

  if (!Number.isFinite(numeric) || numeric <= 0) {
    return roundQty(fallback);
  }

  return roundQty(numeric);
};

const sanitizeBatchCount = (value) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return 1;
  }

  return parsed;
};

const buildBatchRows = (totalQty, batchCount) => {
  const safeBatchCount = sanitizeBatchCount(batchCount);
  const safeTotalQty = Math.max(roundQty(totalQty), 0);

  if (safeBatchCount === 1) {
    return [{ batch_no: 1, qty: safeTotalQty }];
  }

  const baseQty = roundQty(safeTotalQty / safeBatchCount);
  let allocatedQty = 0;

  return Array.from({ length: safeBatchCount }, (_, index) => {
    const batchNo = index + 1;
    const qty =
      batchNo === safeBatchCount
        ? roundQty(Math.max(safeTotalQty - allocatedQty, 0))
        : baseQty;

    allocatedQty = roundQty(allocatedQty + qty);

    return {
      batch_no: batchNo,
      qty,
    };
  });
};

const buildSauceBatchRows = (
  totalSauceWeight,
  batchCount,
  sauceBatchWeight = 0,
) => {
  const safeBatchCount = sanitizeBatchCount(batchCount);
  const safeTotalSauceWeight = Math.max(roundQty(totalSauceWeight), 0);
  const safeSauceBatchWeight = sanitizeSauceBatchWeight(
    sauceBatchWeight,
    safeBatchCount > 0
      ? safeTotalSauceWeight / safeBatchCount
      : safeTotalSauceWeight,
  );

  if (safeBatchCount === 1) {
    return [{ batch_no: 1, sauce_weight: safeTotalSauceWeight }];
  }

  let allocatedSauceWeight = 0;

  return Array.from({ length: safeBatchCount }, (_, index) => {
    const batchNo = index + 1;
    const sauceWeight =
      batchNo === safeBatchCount
        ? roundQty(Math.max(safeTotalSauceWeight - allocatedSauceWeight, 0))
        : roundQty(
            Math.min(
              safeSauceBatchWeight,
              Math.max(safeTotalSauceWeight - allocatedSauceWeight, 0),
            ),
          );

    allocatedSauceWeight = roundQty(allocatedSauceWeight + sauceWeight);

    return {
      batch_no: batchNo,
      sauce_weight: sauceWeight,
    };
  });
};

const buildBatchPlan = (record, totalQty, batchCount, sauceBatchWeight) => {
  const qtyRows = buildBatchRows(totalQty, batchCount);
  const sauceRows = buildSauceBatchRows(
    getTotalPreparedSauceWeight(record, totalQty),
    batchCount,
    sauceBatchWeight,
  );

  return qtyRows.map((batch, index) => ({
    ...batch,
    sauce_weight: sauceRows[index]?.sauce_weight || 0,
  }));
};

const getBatchTotal = (batches = []) =>
  roundQty(batches.reduce((total, item) => total + Number(item?.qty || 0), 0));

const getBatchCountByPreparedSauceWeight = (
  preparedSauceWeight,
  sauceBatchWeight,
  fallbackBatchCount = 1,
) => {
  const normalizedSauceBatchWeight = sanitizeSauceBatchWeight(
    sauceBatchWeight,
    0,
  );

  if (preparedSauceWeight <= 0 || normalizedSauceBatchWeight <= 0) {
    return sanitizeBatchCount(fallbackBatchCount);
  }

  return sanitizeBatchCount(
    Math.ceil(preparedSauceWeight / normalizedSauceBatchWeight),
  );
};

const normalizeRecordWithBatches = (record, options = {}) => {
  const { resetSauceBatchWeight = false, useStoredBatchCount = false } = options;
  const initialQty = Math.max(roundQty(getDefaultQty(record)), 0);
  const sourceBatches = Array.isArray(record?.batches) ? record.batches : [];
  const fallbackBatchCount = sanitizeBatchCount(
    record?.batch_count || sourceBatches.length || 1,
  );
  const sourceBatchCount =
    sourceBatches.length > 0 ? sourceBatches.length : fallbackBatchCount;
  const preparedSauceWeight = getTotalPreparedSauceWeight(record, initialQty);
  const defaultSauceBatchWeight = preparedSauceWeight;
  const sauceBatchWeight = resetSauceBatchWeight
    ? defaultSauceBatchWeight
    : sanitizeSauceBatchWeight(
        record?.sauce_batch_weight,
        defaultSauceBatchWeight,
      );
  const batchCount = useStoredBatchCount
    ? fallbackBatchCount
    : getBatchCountByPreparedSauceWeight(
        preparedSauceWeight,
        sauceBatchWeight,
        sourceBatchCount,
      );

  const shouldReuseSourceBatches =
    !resetSauceBatchWeight && sourceBatches.length === batchCount;

  const batches = shouldReuseSourceBatches
    ? sourceBatches.map((item, index) => ({
        batch_no: index + 1,
        qty: Math.max(roundQty(item?.qty), 0),
        sauce_weight: Math.max(roundQty(item?.sauce_weight), 0),
        production_date: item?.production_date || null,
      }))
    : buildBatchPlan(record, initialQty, batchCount, sauceBatchWeight);

  /* จำนวนผลิตยึดค่าที่ผู้ใช้กรอก/บันทึกไว้ (record.qty) เป็นหลัก
     ห้ามคิดจากผลรวม batch เพราะ batch.qty ของ PD ที่บันทึกแล้วเก็บเป็นน้ำหนักซอส (kg)
     เดิมเปิด PD มาแก้ จำนวนผลิตจึงเด้งกลับเป็นน้ำหนักซอสรวม (เช่น 2520 → 549.71) */
  const plannedQty = initialQty > 0 ? initialQty : getBatchTotal(batches);
  const normalizedPreparedSauceWeight = getPreparedSauceWeight(
    record,
    plannedQty,
  );

  return {
    ...record,
    batch_count: batchCount,
    sauce_weight: getTotalSauceWeight(record, plannedQty),
    prepared_sauce_weight: normalizedPreparedSauceWeight,
    sauce_batch_weight: sauceBatchWeight,
    batches,
    qty: plannedQty,
  };
};

const normalizeRecordsWithBatches = (records = [], options = {}) =>
  records.map((record) => normalizeRecordWithBatches(record, options));

const normalizeRecords = (config) => {
  const records = Array.isArray(config?.records)
    ? config.records
    : config?.record
      ? [config.record]
      : [];

  return records.filter(Boolean);
};

const relabelProcessStepText = (value) => {
  if (!value) {
    return value;
  }

  return String(value).replace(/กลุ่มที่\s*(\d+)/g, "step ที่ $1");
};

function ProductionOrderManage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = location.state || { config: null };
  const initialRecords = normalizeRecordsWithBatches(normalizeRecords(config));
  const initialRecord = initialRecords[0] || null;
  const [form] = Form.useForm();
  const loggedPageRef = useRef(null);
  const reprocessTimerRef = useRef(null);

  const [selectedRecords, setSelectedRecords] = useState(initialRecords);
  const [previewData, setPreviewData] = useState(null);
  const [masterProcessData, setMasterProcessData] = useState({
    header: null,
    detail: [],
  });
  const [loading, setLoading] = useState(false);
  const [productionQty, setProductionQty] = useState(0);
  const [isPendingReprocess, setIsPendingReprocess] = useState(false);
  const [openSelectorModal, setOpenSelectorModal] = useState(false);
  const [totalSauceBatchWeight, setTotalSauceBatchWeight] = useState(
    initialRecord?.prepared_sauce_weight || 0,
  );
  const [sauceBatchWeightInput, setSauceBatchWeightInput] = useState(
    initialRecord?.prepared_sauce_weight || 0,
  );
  // ค่าที่ผู้ใช้พิมพ์ไว้ใน "จำนวนผลิตต่อ batch (kg)" — เป็นค่า draft แยกจาก "น้ำหนักซอสต่อครั้ง" จะมีผลจริงต่อเมื่อกด "ประมวลผล"
  const [batchWeightDraft, setBatchWeightDraft] = useState(
    initialRecord?.prepared_sauce_weight || 0,
  );
  const [lotAllocations, setLotAllocations] = useState({});
  const [editableMaterials, setEditableMaterials] = useState([]);
  const isViewMode = config?.action === "view";
  const [editLoading, setEditLoading] = useState(
    config?.action === "edit" || config?.action === "view",
  );
  // config?.action === "create" && !initialRecord,
  // );
  const produceType = DEFAULT_PRODUCE_TYPE;

  const selectedRecord = useMemo(
    () => selectedRecords[0] || null,
    [selectedRecords],
  );

  /* เลือกหลาย Job Order (FG เดียวกัน) → จำนวนผลิต = ผลรวมของทุกรายการ */
  const selectedQtyTotal = useMemo(
    () => roundQty(selectedRecords.reduce((sum, r) => sum + getDefaultQty(r), 0)),
    [selectedRecords],
  );
  const selectedQtyKey = selectedRecords
    .map((r) => `${r?.detail_code}:${getDefaultQty(r)}`)
    .join("|");

  useEffect(() => {
    const nextRecords = normalizeRecordsWithBatches(normalizeRecords(config));
    setSelectedRecords(nextRecords);
  }, [config]);

  useEffect(() => {
    const action = config?.action;
    if (!action) {
      return;
    }

    const logKey = `${action}:${initialRecord?.detail_code || initialRecord?.pdcode || "new"}:${location.pathname}`;
    if (loggedPageRef.current === logKey) {
      return;
    }

    loggedPageRef.current = logKey;

    productionOrderService
      .logAction({
        action,
        module: "product-order",
        page_path: location.pathname,
        route_action: action,
        title: config?.title || null,
        target: {
          detail_code: initialRecord?.detail_code || null,
          pdcode: initialRecord?.pdcode || null,
          jocode: initialRecord?.jocode || null,
          pg_code: initialRecord?.pg_code || null,
          fg_code: initialRecord?.fg_code || null,
          produce_type: config?.produceType || DEFAULT_PRODUCE_TYPE,
        },
      })
      .catch(() => {});
  }, [
    config?.action,
    config?.produceType,
    config?.title,
    initialRecord?.detail_code,
    initialRecord?.fg_code,
    initialRecord?.jocode,
    initialRecord?.pdcode,
    initialRecord?.pg_code,
    location.pathname,
  ]);

  useEffect(() => {
    const isLoadAction = config?.action === "edit" || config?.action === "view";
    if (!isLoadAction || !config?.record?.pdcode) {
      setEditLoading(false);
      return;
    }

    let ignore = false;
    setEditLoading(true);

    productionOrderService
      .get(config.record.pdcode, { ignoreLoading: true })
      .then((res) => {
        if (ignore) return;

        const data = res?.data?.data;
        const rawOrders = Array.isArray(data?.orders) ? data.orders : [];
        const rawBatches = Array.isArray(data?.batches) ? data.batches : [];
        const orders = normalizeRecordsWithBatches(
          rawOrders.map((order, index) => {
            const baseOrder = {
              ...order,
              sauce_batch_weight: Number(order.sauce_weight) || undefined,
            };
            if (index === 0 && rawBatches.length > 0) {
              const totalQty = Number(order.qty || 0);
              const qtyRows = buildBatchRows(totalQty, rawBatches.length);
              const storedBatches = rawBatches.map((b, bi) => ({
                batch_no: Number(b.batch_no),
                sauce_weight: Number(b.weight || 0),
                qty: Number(b.qty) > 0 ? Number(b.qty) : (qtyRows[bi]?.qty ?? 0),
                production_date: b.production_date || null,
              }));
              return { ...baseOrder, batches: storedBatches };
            }
            return baseOrder;
          }),
          { useStoredBatchCount: true },
        );

        if (!orders.length) {
          message.error("ไม่พบข้อมูลรายการใน Production Order นี้");
          navigate(gotoFrom, { replace: true });
          return;
        }

        setSelectedRecords(orders);

        // derive pdDates from stored batches
        const dateCountMap = {};
        rawBatches.forEach((b) => {
          if (b.production_date) {
            dateCountMap[b.production_date] = (dateCountMap[b.production_date] || 0) + 1;
          }
        });
        const derivedDates = Object.keys(dateCountMap)
          .sort()
          .map((date) => ({ date, batch_count: dateCountMap[date] }));
        if (derivedDates.length > 0) {
          setPdDates(derivedDates);
          setActivePdDateTab(derivedDates[0].date);
        }

        if (data?.header?.pddate) {
          form.setFieldsValue({ pd_date: dayjs(data.header.pddate) });
        }
      })
      .catch(() => {
        if (!ignore) {
          message.error("โหลดข้อมูล Production Order ไม่สำเร็จ");
          navigate(gotoFrom, { replace: true });
        }
      })
      .finally(() => {
        if (!ignore) setEditLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [config?.action, config?.record?.pdcode]);

  useEffect(() => {
    if (!selectedRecord) {
      if (config?.action !== "create") {
        navigate(gotoFrom, { replace: true });
      }
      return;
    }

    const defaultQty = selectedQtyTotal;
    setProductionQty(defaultQty);
    setIsPendingReprocess(false);
    setPreviewData(null);
    form.setFieldsValue({
      pd_date: form.getFieldValue("pd_date") || dayjs(),
      production_qty: defaultQty,
    });
  }, [config?.action, form, navigate, selectedQtyKey]);

  useEffect(() => {
    if (!selectedRecord?.fg_code) {
      setMasterProcessData({ header: null, detail: [] });
      return;
    }

    let ignore = false;

    masterProcessService
      .get(selectedRecord.fg_code)
      .then((res) => {
        if (ignore) {
          return;
        }

        const data = res?.data?.data || {};
        const detail = Array.isArray(data?.detail)
          ? data.detail.map((item) => ({
              ...item,
              qty: item?.qty ?? item?.control_value ?? 0,
            }))
          : [];

        setMasterProcessData({
          header: data?.header || null,
          detail,
        });
      })
      .catch(() => {
        if (!ignore) {
          setMasterProcessData({ header: null, detail: [] });
        }
      });

    return () => {
      ignore = true;
    };
  }, [selectedRecord?.fg_code]);

  const loadPreview = async (override = {}) => {
    if (!selectedRecord) {
      return;
    }

    const nextProductionQty = Number(
      override.productionQty ?? productionQty ?? 0,
    );

    if (nextProductionQty <= 0) {
      message.error("กรุณาระบุจำนวนที่ต้องการผลิต");
      return;
    }

    setLoading(true);
    try {
      const res = await productionOrderService.preview({
        ...selectedRecord,
        produce_type: DEFAULT_PRODUCE_TYPE,
        target_code: "",
        production_qty: nextProductionQty,
        use_bom: override.useBom ? 1 : 0,
      });

      const data = res?.data?.data;
      if (data?.need_bom_confirm) {
        setPreviewData(null);
        Modal.confirm({
          title: "ไม่พบ Master Process",
          content: `สินค้า ${data?.job_order?.fg_code || ""} ยังไม่มี Master Process ต้องการใช้ BOM คำนวณแทนหรือไม่?`,
          okText: "ใช้ BOM คำนวณ",
          cancelText: "ยกเลิก",
          onOk: () => loadPreview({ ...override, productionQty: nextProductionQty, useBom: true }),
        });
        return;
      }
      setPreviewData(data);
      console.log("Preview Data:", data);
      setIsPendingReprocess(false);
    } catch (err) {
      const data = err?.response?.data;
      message.error(data?.message || "ตรวจสอบความพร้อมการผลิตไม่สำเร็จ");
      setPreviewData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!previewData) {
      message.error("กรุณาประมวลผลข้อมูลก่อนบันทึก");
      return;
    }

    if (isPendingReprocess) {
      message.error("มีการเปลี่ยนข้อมูล กรุณาประมวลผลใหม่ก่อนบันทึก");
      return;
    }

    form
      .validateFields()
      .then((values) => {
        const payload = {
          action: config?.action || "create",
          pdcode: config?.record?.pdcode || null,
          pddate: values?.pd_date
            ? dayjs(values.pd_date).format("YYYY-MM-DD")
            : null,
          doc_status: "รอดำเนินการ",
          orders: selectedRecords.map((record, index) => ({
            ...record,
            batch: index === 0 ? (record?.batch_count || 0) : 0,
            qty: index === 0 ? (record?.qty || 0) : 0,
            seq: index + 1,
            sauce_weight: index === 0 ? (sauceBatchWeightInput || 0) : 0,
            batches: index === 0 ? (record?.batches || []) : [],
          })),
          issue_items: allocationValidation.rows.map((row) => ({
            item_group: row.item_group,
            mat_code: row.mat_code,
            mat_name: row.mat_name,
            required_qty: roundStockQty(row.required_qty),
            allocations: row.allocations
              .filter(
                (allocation) => roundStockQty(allocation?.issue_qty || 0) > 0,
              )
              .map((allocation) => ({
                stock_id: allocation.stock_id,
                issue_qty: roundStockQty(allocation.issue_qty),
              })),
          })),
        };

        productionOrderService
          .manage(payload)
          .then((res) => {
            const nextPdcode = res?.data?.data?.pdcode;
            message.success(
              nextPdcode ? `บันทึกสำเร็จ: ${nextPdcode}` : "บันทึกสำเร็จ",
            );
            navigate(gotoFrom, { replace: true });
          })
          .catch((err) => {
            const data = err?.response?.data;
            message.error(data?.message || "บันทึก Production Order ไม่สำเร็จ");
          });
      })
      .catch(() => {
        message.error("กรุณาตรวจสอบข้อมูลก่อนบันทึก");
      });
  };

  useEffect(() => {
    if (!selectedRecord) {
      return;
    }

    const defaultQty = selectedQtyTotal;
    if (defaultQty > 0) {
      loadPreview({
        productionQty: defaultQty,
      });
    }

  }, [selectedQtyKey]);

  // auto-add วันนี้เมื่อเลือก JO ครั้งแรก (create mode เท่านั้น)
  useEffect(() => {
    if (!selectedRecord || config?.action !== "create" || pdDates.length > 0) return;
    const today = dayjs().format("YYYY-MM-DD");
    const next = [{ date: today, batch_count: 1 }];
    setPdDates(next);
    applyPdDatesToBatches(next);
    setActivePdDateTab(today);
  }, [selectedRecord?.detail_code]);

  const handleBack = () => {
    navigate(gotoFrom, { replace: true });
  };

  const handleQtyChange = (nextQty) => {
    if (!selectedRecord) return;
    const normalizedQty = Math.max(roundQty(nextQty), 0);

    if (reprocessTimerRef.current) clearTimeout(reprocessTimerRef.current);

    setSelectedRecords((currentRecords) =>
      currentRecords.map((item, index) =>
        index === 0
          ? normalizeRecordWithBatches(
              { ...item, qty: normalizedQty, batches: [] },
              { resetSauceBatchWeight: true },
            )
          : item,
      ),
    );

    setProductionQty(normalizedQty);
    form.setFieldValue("production_qty", normalizedQty);

    if (previewData) {
      reprocessTimerRef.current = setTimeout(() => {
        loadPreview({ productionQty: normalizedQty });
      }, 600);
    } else {
      setIsPendingReprocess(true);
    }
  };

  const handleSauceBatchWeightChange = (nextSauceBatchWeight, totalOverride = null) => {
    if (!selectedRecord) {
      return;
    }

    const safeSauceBatchWeight = Math.max(roundQty(nextSauceBatchWeight), 0);

    if (safeSauceBatchWeight <= 0) {
      setSelectedRecords((currentRecords) =>
        currentRecords.map((item) =>
          item?.detail_code === selectedRecord?.detail_code
            ? { ...item, sauce_batch_weight: 0 }
            : item,
        ),
      );
      return;
    }

    const existingBatchSauceTotal = roundQty(
      (selectedRecord?.batches || []).reduce(
        (sum, b) => sum + Number(b?.sauce_weight || 0),
        0,
      ),
    );

    const safeTotalSauceBatchWeight = Math.max(
      roundQty(
        totalOverride !== null
          ? totalOverride
          : (totalSauceBatchWeight || selectedRecord?.prepared_sauce_weight || existingBatchSauceTotal),
      ),
      0,
    );

    const targetBatchCount = safeTotalSauceBatchWeight > 0
      ? sanitizeBatchCount(Math.ceil(safeTotalSauceBatchWeight / safeSauceBatchWeight))
      : sanitizeBatchCount(selectedRecord?.batch_count || 1);

    const baseQtyRows = buildBatchRows(
      Math.max(roundQty(selectedRecord?.qty || 0), 0),
      targetBatchCount,
    );

    let allocatedSauceWeight = 0;

    const nextBatches = baseQtyRows.map((batch, index) => {
      const isLastBatch = index === targetBatchCount - 1;
      const useRemainder =
        safeTotalSauceBatchWeight > 0 &&
        safeSauceBatchWeight < safeTotalSauceBatchWeight &&
        isLastBatch;
      const sauceWeight = useRemainder
        ? roundQty(Math.max(safeTotalSauceBatchWeight - allocatedSauceWeight, 0))
        : safeSauceBatchWeight;

      allocatedSauceWeight = roundQty(allocatedSauceWeight + sauceWeight);

      return {
        ...batch,
        sauce_weight: sauceWeight,
      };
    });

    setSelectedRecords((currentRecords) =>
      currentRecords.map((item) =>
        item?.detail_code === selectedRecord?.detail_code
          ? {
              ...item,
              batch_count: targetBatchCount,
              sauce_batch_weight: safeSauceBatchWeight,
              batches: nextBatches,
            }
          : item,
      ),
    );
  };

  // กดปุ่ม "ประมวลผล" จาก "จำนวนผลิตต่อ batch (kg)" — แบ่ง "น้ำหนักซอสต่อครั้ง (kg)" ออกเป็น batch ละเท่าๆกัน (เศษไปไว้ batch สุดท้าย)
  // อ่านค่า sauceBatchWeightInput เพื่อใช้เป็นฐานรวมเท่านั้น ไม่แก้ไข/setSauceBatchWeightInput หรือ selectedRecord.sauce_batch_weight เด็ดขาด
  const handleProcessBatchPlan = (nextBatchWeight) => {
    if (!selectedRecord) return;

    const safeBatchWeight = Math.max(roundQty(nextBatchWeight), 0);
    if (safeBatchWeight <= 0) return;

    const totalSauceWeightBasis = Math.max(roundQty(sauceBatchWeightInput || 0), 0);
    if (totalSauceWeightBasis <= 0) return;

    const existingBatches = selectedRecord?.batches || [];

    // จำนวน batch = ceil(น้ำหนักซอสต่อครั้ง / จำนวนผลิตต่อ batch) — แต่ละ batch ได้เท่ากับค่าที่กรอก ยกเว้น batch สุดท้ายรับเศษที่เหลือ
    const targetBatchCount = sanitizeBatchCount(
      Math.ceil(totalSauceWeightBasis / safeBatchWeight),
    );

    let allocated = 0;
    const nextBatches = Array.from({ length: targetBatchCount }, (_, index) => {
      const batchNo = index + 1;
      const isLastBatch = batchNo === targetBatchCount;
      const amount = isLastBatch
        ? roundQty(Math.max(totalSauceWeightBasis - allocated, 0))
        : safeBatchWeight;

      allocated = roundQty(allocated + amount);

      const existing = existingBatches.find((b) => b.batch_no === batchNo);

      return {
        batch_no: batchNo,
        qty: amount,
        sauce_weight: amount,
        production_date: existing?.production_date || null,
      };
    });

    setSelectedRecords((currentRecords) =>
      currentRecords.map((item) =>
        item?.detail_code === selectedRecord?.detail_code
          ? { ...item, batch_count: targetBatchCount, batches: nextBatches }
          : item,
      ),
    );

    // sync จำนวน batch ของแต่ละวันใน "วันผลิต" ให้รวมเท่ากับ targetBatchCount ที่ประมวลผลได้ใหม่ (ตัด warning รวม X/Y batch)
    if (pdDates.length > 0) {
      const oldTotal = pdDates.reduce(
        (sum, d) => sum + Number(d.batch_count || 0),
        0,
      );
      let nextPdDates;

      if (pdDates.length === 1) {
        nextPdDates = [{ ...pdDates[0], batch_count: targetBatchCount }];
      } else if (oldTotal > 0) {
        let allocatedDayCount = 0;
        nextPdDates = pdDates.map((d, index) => {
          const isLast = index === pdDates.length - 1;
          const share = isLast
            ? Math.max(targetBatchCount - allocatedDayCount, 0)
            : Math.round((Number(d.batch_count || 0) / oldTotal) * targetBatchCount);
          allocatedDayCount += share;
          return { ...d, batch_count: share };
        });
      } else {
        // ยังไม่มี batch ระบุไว้เลยสักวัน → ใส่ทั้งหมดไว้วันแรก
        nextPdDates = pdDates.map((d, index) => ({
          ...d,
          batch_count: index === 0 ? targetBatchCount : 0,
        }));
      }

      setPdDates(nextPdDates);
      applyPdDatesToBatches(nextPdDates);
    }
  };

  // ผู้ใช้แก้ไข "จำนวน Batch" เองโดยตรง (เช่น หลังกดประมวลผลแล้วอยากปรับ) → rebuild batches ใหม่ตามจำนวนที่ระบุ
  const handleManualBatchCountChange = (nextCount) => {
    if (!selectedRecord) return;

    const targetBatchCount = sanitizeBatchCount(nextCount);
    const totalQty = Math.max(roundQty(selectedRecord?.qty || 0), 0);
    const existingBatches = selectedRecord?.batches || [];
    const nextBatches = buildBatchPlan(
      selectedRecord,
      totalQty,
      targetBatchCount,
      sauceBatchWeightInput,
    ).map((batch) => {
      const existing = existingBatches.find((b) => b.batch_no === batch.batch_no);
      return { ...batch, production_date: existing?.production_date || null };
    });

    setSelectedRecords((currentRecords) =>
      currentRecords.map((item) =>
        item?.detail_code === selectedRecord?.detail_code
          ? { ...item, batch_count: targetBatchCount, batches: nextBatches }
          : item,
      ),
    );
  };

  const handleRemoveRecord = (record) => {
    const removingMain = selectedRecords[0]?.detail_code === record?.detail_code;
    const nextRecords = selectedRecords.filter(
      (item) => item?.detail_code !== record?.detail_code,
    );

    if (removingMain && nextRecords.length > 0) {
      const currentQty = selectedRecord?.qty || 0;
      nextRecords[0] = normalizeRecordWithBatches(
        { ...nextRecords[0], qty: currentQty, batches: [] },
        { resetSauceBatchWeight: true },
      );
    }

    setSelectedRecords(nextRecords);
  };

  const refColumns = useMemo(
    () =>
      jobOrderRefColumns({
        isViewMode,
        onRemoveRecord: isViewMode ? null : handleRemoveRecord,
      }),
    [isViewMode, selectedRecords],
  );

  const handleBatchWeightChange = useCallback((batch, nextWeight) => {
    setSelectedRecords((current) =>
      current.map((item, index) =>
        index === 0
          ? {
              ...item,
              batches: (item.batches || []).map((b) =>
                b.batch_no === batch.batch_no
                  ? { ...b, sauce_weight: roundQty(nextWeight) }
                  : b,
              ),
            }
          : item,
      ),
    );
  }, []);

  /* ลบหม้อเคี่ยว 1 หม้อ
     - เอาแถวนั้นออกแล้วเรียงเลข batch ใหม่ให้ต่อเนื่อง
     - ปรับจำนวนหม้อของวันผลิตนั้นให้ตรงกับที่เหลือจริง
     - ไม่ให้ลบจนไม่เหลือหม้อเลย */
  const handleBatchDelete = useCallback((batch) => {
    setSelectedRecords((current) =>
      current.map((item, index) => {
        if (index !== 0) return item;

        const rows = Array.isArray(item.batches) ? item.batches : [];
        if (rows.length <= 1) {
          message.warning("ต้องมีหม้อเคี่ยวอย่างน้อย 1 หม้อ");
          return item;
        }

        const kept = rows.filter((b) => b.batch_no !== batch.batch_no);
        const renumbered = kept.map((b, i) => ({ ...b, batch_no: i + 1 }));

        return { ...item, batches: renumbered, batch_count: renumbered.length };
      }),
    );

    /* วันผลิตเก็บจำนวนหม้อของแต่ละวันไว้ต่างหาก ต้องลดตามด้วย */
    setPdDates((current) => {
      const target = batch?.production_date || null;
      if (!target) return current;
      return current
        .map((d) =>
          d.date === target
            ? { ...d, batch_count: Math.max(0, Number(d.batch_count || 0) - 1) }
            : d,
        )
        .filter((d) => Number(d.batch_count || 0) > 0);
    });
  }, []);

  const handleBatchQtyChange = useCallback((batchNo, nextQty) => {
    setSelectedRecords((current) =>
      current.map((item, index) =>
        index === 0
          ? {
              ...item,
              batches: (item.batches || []).map((b) =>
                b.batch_no === batchNo
                  ? { ...b, qty: roundQty(Number(nextQty || 0)) }
                  : b,
              ),
            }
          : item,
      ),
    );
  }, []);

  // --- วันผลิต (Production Date Plan) ---
  const [selectedBatchNo, setSelectedBatchNo] = useState(null);
  const [pdDates, setPdDates] = useState([]); // [{date, batch_count}]
  const [activePdDateTab, setActivePdDateTab] = useState("");

  const applyPdDatesToBatches = useCallback((dates) => {
    setSelectedRecords((current) =>
      current.map((item, index) => {
        if (index !== 0) return item;
        let batchNo = 1;
        const dateMap = {};
        for (const d of dates) {
          const count = Number(d.batch_count || 0);
          for (let i = 0; i < count; i++) {
            dateMap[batchNo] = d.date;
            batchNo++;
          }
        }
        return {
          ...item,
          batches: (item.batches || []).map((b) => ({
            ...b,
            production_date: dateMap[b.batch_no] !== undefined ? dateMap[b.batch_no] : b.production_date,
          })),
        };
      }),
    );
  }, []);

  // เพิ่มวัน: เลือกวันจาก DatePicker → เพิ่มเลย, batch_count เริ่มที่ 1
  const handleAddPdDate = useCallback((date) => {
    if (!date || pdDates.some((d) => d.date === date)) return;
    const next = [...pdDates, { date, batch_count: 1 }];
    const newTotal = next.reduce((sum, d) => sum + Number(d.batch_count || 0), 0);
    let batchNo = 1;
    const dateMap = {};
    for (const d of next) {
      const c = Number(d.batch_count || 0);
      for (let i = 0; i < c; i++) { dateMap[batchNo] = d.date; batchNo++; }
    }
    setPdDates(next);
    setSelectedRecords((current) =>
      current.map((item, index) => {
        if (index !== 0) return item;
        const existingBatches = item.batches || [];
        const totalQty = Number(item.qty || 0);
        const qtyPerBatch = newTotal > 0 ? roundQty(totalQty / newTotal) : 0;
        const newBatches = Array.from({ length: newTotal }, (_, i) => {
          const no = i + 1;
          const existing = existingBatches.find((b) => b.batch_no === no);
          return {
            batch_no: no,
            qty: existing?.qty ?? qtyPerBatch,
            sauce_weight: existing?.sauce_weight ?? 0,
            production_date: dateMap[no] || null,
          };
        });
        return { ...item, batch_count: newTotal, batches: newBatches };
      }),
    );
    setActivePdDateTab(date);
  }, [pdDates]);

  // แก้จำนวน batch ของวันนั้น → rebuild batches ให้ตรงกับ total ใหม่
  const handleUpdatePdDateCount = useCallback((date, count) => {
    const next = pdDates.map((d) =>
      d.date === date ? { ...d, batch_count: Number(count || 1) } : d,
    );
    const newTotal = next.reduce((sum, d) => sum + Number(d.batch_count || 0), 0);

    // สร้าง dateMap: batch_no → production_date
    let batchNo = 1;
    const dateMap = {};
    for (const d of next) {
      const c = Number(d.batch_count || 0);
      for (let i = 0; i < c; i++) { dateMap[batchNo] = d.date; batchNo++; }
    }

    setPdDates(next);
    setSelectedRecords((current) =>
      current.map((item, index) => {
        if (index !== 0) return item;
        const existingBatches = item.batches || [];
        const totalQty = Number(item.qty || 0);
        const qtyPerBatch = newTotal > 0 ? roundQty(totalQty / newTotal) : 0;

        const newBatches = Array.from({ length: newTotal }, (_, i) => {
          const no = i + 1;
          const existing = existingBatches.find((b) => b.batch_no === no);
          return {
            batch_no: no,
            qty: existing?.qty ?? qtyPerBatch,
            sauce_weight: existing?.sauce_weight ?? 0,
            production_date: dateMap[no] || null,
          };
        });

        return { ...item, batch_count: newTotal, batches: newBatches };
      }),
    );
  }, [pdDates]);

  const handleRemovePdDate = useCallback((date) => {
    const next = pdDates.filter((d) => d.date !== date);
    setPdDates(next);
    applyPdDatesToBatches(next);
    setActivePdDateTab((cur) => {
      if (cur !== date) return cur;
      return next[0]?.date || "";
    });
  }, [pdDates, applyPdDatesToBatches]);

  const pdDatesTotalBatch = useMemo(
    () => pdDates.reduce((sum, d) => sum + Number(d.batch_count || 0), 0),
    [pdDates],
  );


  const batchColumns = useMemo(
    () =>
      batchPlanningColumns({
        getBatchSauceWeight: (batch) => Number(batch?.sauce_weight || 0),
        onWeightChange: isViewMode ? undefined : handleBatchWeightChange,
        onDelete: isViewMode ? undefined : handleBatchDelete,
        isViewMode,
      }),
    [isViewMode, handleBatchWeightChange, handleBatchDelete],
  );

  const materialShortages = previewData?.shortages?.materials || [];
  const packagingShortages = previewData?.shortages?.packaging || [];
  const summary = previewData?.summary || {};
  const jobOrder = previewData?.job_order || selectedRecord || {};
  const selectedTarget = previewData?.selected_target || {};
  const masterProcess =
    (previewData?.master_process?.detail?.length
      ? previewData?.master_process
      : null) ||
    masterProcessData ||
    {};
  const masterProcessDetails = masterProcess?.detail || [];
  const normalizedMasterProcessDetails = useMemo(() => {
    let previousStepNo = null;

    return masterProcessDetails.map((item) => {
      const currentStepNo = item?.stepno ?? null;
      const displayStepNo =
        currentStepNo === previousStepNo ? "" : currentStepNo;
      const qty = Number(item?.qty || 0);
      const stepRatioPercent = qty > 0 ? qty : null;

      previousStepNo = currentStepNo;

      return {
        ...item,
        displayStepNo,
        stepRatioPercent,
        stname: relabelProcessStepText(item?.stname),
        method: relabelProcessStepText(item?.method),
      };
    });
  }, [masterProcessDetails]);
  const overOrder = Number(summary?.over_order_qty || 0) > 0;
  const sauceWeightPerUnit = getNetWeightPerUnit(jobOrder);
  const sauceWeight = getTotalSauceWeight(
    jobOrder,
    selectedRecord?.qty || 0,
  );
  const preparedSauceWeight = getPreparedSauceWeight(
    jobOrder,
    selectedRecord?.qty || 0,
  );
  const allMaterials = useMemo(
    () => previewData?.materials || [],
    [previewData?.materials],
  );

  // sync editableMaterials เมื่อ preview โหลดใหม่
  useEffect(() => {
    setEditableMaterials(allMaterials);
  }, [allMaterials]);

  const handleMaterialQtyChange = useCallback((matCode, newQty) => {
    setEditableMaterials((current) =>
      current.map((mat) => {
        if (mat.mat_code !== matCode) return mat;
        const qty = Number(newQty || 0);
        const balance = Number(mat.stock || 0) - qty;
        return { ...mat, required_qty: qty, balance, is_enough: balance >= 0 };
      }),
    );
  }, []);

  const allPackaging = useMemo(
    () => previewData?.packaging || [],
    [previewData?.packaging],
  );

  const issueItems = useMemo(
    () => [
      ...editableMaterials.map((item) => ({ ...item, item_group: "material" })),
      ...allPackaging.map((item) => ({ ...item, item_group: "packaging" })),
    ],
    [editableMaterials, allPackaging],
  );

  useEffect(() => {
    setLotAllocations(buildAutoLotAllocationMap(issueItems));
  }, [issueItems]);

  const getItemAllocationRows = (item) =>
    lotAllocations[getIssueItemKey(item)] || [];

  const allocationValidation = useMemo(() => {
    const rows = issueItems.map((item) => {
      const allocations = getItemAllocationRows(item);
      const requiredQty = roundStockQty(item?.required_qty || 0);
      const allocatedQty = roundStockQty(
        allocations.reduce(
          (total, allocation) =>
            total + roundStockQty(allocation?.issue_qty || 0),
          0,
        ),
      );
      const remainingQty = roundStockQty(requiredQty - allocatedQty);
      const hasOverAllocatedLot = allocations.some(
        (allocation) =>
          roundStockQty(allocation?.issue_qty || 0) >
          roundStockQty(allocation?.available_qty || 0),
      );

      return {
        ...item,
        required_qty: requiredQty,
        allocated_qty: allocatedQty,
        remaining_qty: remainingQty,
        allocations,
        hasMismatch: Math.abs(remainingQty) > 0.01,
        hasOverAllocatedLot,
      };
    });

    const hasShortages = !(
      summary?.can_cover_materials && summary?.can_cover_packaging
    );
    return {
      rows,
      hasShortages,
      isValid:
        !hasShortages &&
        rows.every((row) => !row.hasMismatch && !row.hasOverAllocatedLot),
    };
  }, [
    issueItems,
    lotAllocations,
    summary?.can_cover_materials,
    summary?.can_cover_packaging,
  ]);

  const handleAutoAllocateItem = (item) => {
    setLotAllocations((current) => ({
      ...current,
      [getIssueItemKey(item)]: buildAutoLotAllocationRows(item),
    }));
  };

  const handleLotIssueQtyChange = (item, stockId, nextValue) => {
    const itemKey = getIssueItemKey(item);
    setLotAllocations((current) => ({
      ...current,
      [itemKey]: (current[itemKey] || []).map((allocation) => {
        if (allocation.stock_id !== stockId) {
          return allocation;
        }

        return {
          ...allocation,
          issue_qty: Math.min(
            Math.max(roundStockQty(nextValue), 0),
            roundStockQty(allocation?.available_qty || 0),
          ),
        };
      }),
    }));
  };

  const lotAllocationContent = (
    <Card>
      <Flex justify="space-between" align="center" wrap gap={8}>
        <Typography.Title level={4} className="m-0">
          Lot Allocation
        </Typography.Title>
        <Tag color={allocationValidation.isValid ? "success" : "error"}>
          {allocationValidation.isValid
            ? "พร้อมตัดสต๊อก"
            : "กรุณาระบุ lot ให้ครบ"}
        </Tag>
      </Flex>
      <Divider />
      {allocationValidation.hasShortages && (
        <Alert
          className="mb-3"
          type="error"
          showIcon
          message="สต๊อกไม่เพียงพอ จึงยังไม่สามารถยืนยันตัดสต๊อกได้"
        />
      )}
      {!allocationValidation.hasShortages &&
        allocationValidation.rows.some((row) => row.hasMismatch) && (
          <Alert
            className="mb-3"
            type="warning"
            showIcon
            message="บางรายการยังจัด lot ไม่ครบตามจำนวนที่ต้องใช้"
          />
        )}
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        {allocationValidation.rows.map((item) => (
          <Card
            key={getIssueItemKey(item)}
            size="small"
            title={`${item.mat_code} : ${item.mat_name}`}
            extra={
              <Space>
                <Tag color={item.item_group === "material" ? "blue" : "purple"}>
                  {item.item_group === "material" ? "Material" : "Packaging"}
                </Tag>
                <Typography.Text
                  type={item.hasMismatch ? "danger" : "secondary"}
                >
                  ใช้ {formatMoney(item.allocated_qty, 2)} /{" "}
                  {formatMoney(item.required_qty, 2)}
                </Typography.Text>
                <Button
                  size="small"
                  onClick={() => handleAutoAllocateItem(item)}
                >
                  Auto Allocate
                </Button>
              </Space>
            }
          >
            <Table
              size="small"
              rowKey="stock_id"
              pagination={false}
              dataSource={item.allocations}
              scroll={{ x: "max-content" }}
              columns={[
                {
                  title: "Lot ID",
                  dataIndex: "stock_id",
                  key: "stock_id",
                  width: 90,
                },
                {
                  title: "Location",
                  dataIndex: "location",
                  key: "location",
                  width: 120,
                  render: (value) => value || "-",
                },
                {
                  title: "Stock Date",
                  dataIndex: "stock_date",
                  key: "stock_date",
                  width: 120,
                  render: formatLotDate,
                },
                {
                  title: "Expire Date",
                  dataIndex: "expire_date",
                  key: "expire_date",
                  width: 120,
                  render: formatLotDate,
                },
                {
                  title: "Available",
                  dataIndex: "available_qty",
                  key: "available_qty",
                  width: 120,
                  align: "right",
                  render: (value) => formatMoney(Number(value || 0), 2),
                },
                {
                  title: "Issue Qty",
                  dataIndex: "issue_qty",
                  key: "issue_qty",
                  width: 160,
                  align: "right",
                  render: (_, allocation) => (
                    <InputNumber
                      min={0}
                      max={roundStockQty(allocation?.available_qty || 0)}
                      step={0.01}
                      style={{ width: "100%" }}
                      value={roundStockQty(allocation?.issue_qty || 0)}
                      onChange={(value) =>
                        handleLotIssueQtyChange(
                          item,
                          allocation.stock_id,
                          value,
                        )
                      }
                    />
                  ),
                },
              ]}
              locale={{ emptyText: "ไม่มี stock lot ให้เลือก" }}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4} align="right">
                    คงเหลือที่ยังต้อง allocate
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    {formatMoney(
                      item.allocations.reduce(
                        (total, allocation) =>
                          total + roundStockQty(allocation?.available_qty || 0),
                        0,
                      ),
                      2,
                    )}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <Typography.Text
                      type={item.hasMismatch ? "danger" : "success"}
                    >
                      {formatMoney(item.remaining_qty, 2)}
                    </Typography.Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Card>
        ))}
      </Space>
    </Card>
  );

  useEffect(() => {
    if (!selectedRecord) return;

    // โหมด edit/view ที่โหลดข้อมูลซึ่งมี batch บันทึกไว้แล้วจริงจาก backend -> ใช้ข้อมูลที่โหลดมาตรงๆ
    // ห้าม recompute/เขียนทับ batches ด้วยสูตรประมาณการ (preparedSauceWeight มาจาก qty หัวบิลซึ่งอาจไม่ตรงกับยอดรวม batch ที่บันทึกจริง)
    const isLoadedRecord = config?.action === "edit" || config?.action === "view";
    const existingBatches = selectedRecord?.batches || [];

    if (isLoadedRecord && existingBatches.length > 0) {
      const existingBatchTotal = roundQty(
        existingBatches.reduce((sum, b) => sum + Number(b?.qty || 0), 0),
      );
      const loadedTotal = roundQty(selectedRecord?.sauce_batch_weight || existingBatchTotal);
      setTotalSauceBatchWeight(loadedTotal);
      setSauceBatchWeightInput(loadedTotal);
      setBatchWeightDraft(Math.ceil(roundQty(existingBatches[0]?.qty || loadedTotal)));
      return;
    }

    const preparedWeight = roundQty(preparedSauceWeight);
    const batchWeight = roundQty(selectedRecord?.sauce_batch_weight || preparedWeight);
    setTotalSauceBatchWeight(preparedWeight);
    setSauceBatchWeightInput(batchWeight);
    // จำนวนผลิตต่อ batch (kg) ดึงมาจากน้ำหนักซอสต่อครั้ง (kg) แต่ปัดทศนิยมขึ้น (ceil)
    setBatchWeightDraft(Math.ceil(batchWeight));

    if (preparedWeight > 0 && batchWeight > 0) {
      const defaultQty = roundQty(selectedRecord?.qty || 0);
      const targetBatchCount = sanitizeBatchCount(
        selectedRecord?.batch_count || Math.ceil(preparedWeight / batchWeight),
      );
      const qtyRows = buildBatchRows(Math.max(defaultQty, 0), targetBatchCount);
      const sauceRows = buildSauceBatchRows(preparedWeight, targetBatchCount, batchWeight);
      const nextBatches = qtyRows.map((batch, index) => {
        const existing = existingBatches.find((b) => b.batch_no === batch.batch_no);
        const sauceWeight = sauceRows[index]?.sauce_weight || 0;
        return {
          ...batch,
          // Batch table เอาค่า "น้ำหนักซอสต่อครั้ง (kg)" มาใส่เป็นค่าเริ่มต้นของแต่ละ batch เสมอ (เศษไว้ batch สุดท้าย)
          qty: sauceWeight,
          sauce_weight: sauceWeight,
          production_date: existing?.production_date || null,
        };
      });
      setSelectedRecords((current) =>
        current.map((item) =>
          item?.detail_code === selectedRecord?.detail_code
            ? { ...item, batch_count: targetBatchCount, sauce_batch_weight: batchWeight, batches: nextBatches }
            : item,
        ),
      );
    }
  }, [preparedSauceWeight, selectedRecord?.detail_code]);

  const handleSelectRecord = (records) => {
    const nextRecords = normalizeRecordsWithBatches(
      Array.isArray(records) ? records.filter(Boolean) : [],
    );

    if (!nextRecords.length) {
      return;
    }

    const lockedFgCode = selectedRecords[0]?.fg_code || nextRecords[0]?.fg_code;
    const compatible = nextRecords.filter((r) => r.fg_code === lockedFgCode);
    const skipped = nextRecords.length - compatible.length;

    if (skipped > 0) {
      message.warning(
        `มี ${skipped} รายการที่ FG Code ไม่ตรงกัน (${lockedFgCode}) และถูกข้ามไป`,
      );
    }

    if (!compatible.length) {
      return;
    }

    const mergedRecords =
      config?.action === "create"
        ? [
            ...selectedRecords,
            ...compatible.filter(
              (nextRecord) =>
                !selectedRecords.some(
                  (currentRecord) =>
                    currentRecord?.detail_code === nextRecord?.detail_code,
                ),
            ),
          ]
        : compatible;

    setSelectedRecords(mergedRecords);
    setOpenSelectorModal(false);
  };
  

  const batchSauceTotal = roundQty(
    (selectedRecord?.batches || []).reduce(
      (sum, b) => sum + Number(b?.sauce_weight || 0),
      0,
    ),
  );

  // ยอดรวม qty ของทุก batch (ทุกวัน) ที่ผ่านการประมวลผลแล้ว — ใช้เป็นฐานสัดส่วน แทน selectedRecord.qty (จำนวนผลิต-Unit)
  // เพราะ batch.qty หลังกด "ประมวลผล" เป็นหน่วย kg คนละหน่วยกับ qty หัวบิล ทำให้สัดส่วนรวมกันไม่ครบ 100%
  const totalAllBatchesQty = roundQty(
    (selectedRecord?.batches || []).reduce(
      (sum, b) => sum + Number(b?.qty || 0),
      0,
    ),
  );

  // คำนวณ materials ของทุก batch ล่วงหน้า keyed by batch_no
  const allBatchMaterials = useMemo(() => {
    return Object.fromEntries(
      (selectedRecord?.batches || []).map((b) => {
        const batchQty = Number(b.qty || 0);
        const ratio = totalAllBatchesQty > 0 ? batchQty / totalAllBatchesQty : 0;
        return [
          b.batch_no,
          editableMaterials.map((mat) => ({
            ...mat,
            batch_qty: roundQty(Number(mat.required_qty || 0) * ratio),
          })),
        ];
      }),
    );
  }, [selectedRecord?.batches, totalAllBatchesQty, editableMaterials]);

  // batch ทั้งหมดที่อยู่ใน "วันผลิต" ที่กำลังเลือกอยู่ (fallback เป็น batch ทั้งหมด ถ้ายังไม่มี batch ไหนระบุวันที่ตรง tab)
  const activeDayBatches = useMemo(() => {
    const allBatches = selectedRecord?.batches || [];
    if (!activePdDateTab || pdDates.length === 0) return allBatches;
    const filteredByDate = allBatches.filter(
      (b) => b.production_date === activePdDateTab,
    );
    return filteredByDate.length > 0 ? filteredByDate : allBatches;
  }, [selectedRecord?.batches, activePdDateTab, pdDates.length]);

  // material ที่ใช้เฉพาะ "วันผลิต" ที่กำลังเลือกอยู่ = required_qty รวม x สัดส่วน batch ของวันนั้นเทียบกับทุก batch
  const dayMaterials = useMemo(() => {
    if (!activePdDateTab) return editableMaterials;
    const dayBatchTotal = roundQty(
      activeDayBatches.reduce((sum, b) => sum + Number(b?.qty || 0), 0),
    );
    const dayRatio = totalAllBatchesQty > 0 ? dayBatchTotal / totalAllBatchesQty : 1;
    return editableMaterials.map((mat) => ({
      ...mat,
      required_qty: roundQty(Number(mat.required_qty || 0) * dayRatio),
    }));
  }, [editableMaterials, activeDayBatches, activePdDateTab, totalAllBatchesQty]);

  const sauceBatchContent = (
    <Card title="หม้อเคี่ยว">
      {selectedRecord ? (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            {/* Batch list */}
            <div style={{ flex: "0 0 auto", minWidth: 200, border: "0.5px solid var(--ant-color-border)", borderRadius: 6, overflow: "hidden" }}>
              <div style={{ padding: "6px 12px", background: "var(--ant-color-fill-quaternary)", fontSize: 12, color: "var(--ant-color-text-secondary)" }}>
                Batch table
              </div>
              {(() => {
                const allBatches = selectedRecord?.batches || [];
                const filteredByDate = allBatches.filter(
                  (b) => b.production_date === activePdDateTab,
                );
                // ถ้ายังไม่มี batch ไหนถูกระบุวันที่ตรงกับ tab ที่เลือก (เช่น เพิ่งกดประมวลผลมาใหม่)
                // ให้แสดง batch ทั้งหมดไปก่อน ไม่ให้ตารางว่างเปล่า
                const batches = !activePdDateTab || pdDates.length === 0 || filteredByDate.length === 0
                  ? allBatches
                  : filteredByDate;
                const totalQty = batches.reduce((sum, b) => sum + Number(b.qty || 0), 0);
                return (
                  <>
                    {batches.map((b, idx) => (
                      <div
                        key={b.batch_no}
                        style={{
                          padding: "8px 12px",
                          borderTop: idx > 0 ? "0.5px solid var(--ant-color-border)" : undefined,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                          background: b.batch_no === selectedBatchNo ? "var(--ant-color-primary-bg)" : undefined,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0, flexWrap: "wrap" }}>
                          <Typography.Text style={{ whiteSpace: "nowrap" }}>
                            Batch {b.batch_no}
                          </Typography.Text>
                          {!isViewMode ? (
                            <InputNumber
                              size="small"
                              min={0}
                              step={1}
                              precision={0}
                              value={Math.round(Number(b.qty || 0))}
                              addonAfter="kg"
                              style={{ width: 120 }}
                              onChange={(v) => handleBatchQtyChange(b.batch_no, v)}
                            />
                          ) : (
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {formatMoney(Number(b.qty || 0), 0)} kg
                            </Typography.Text>
                          )}
                        </div>
                        <Space size={4}>
                          {editableMaterials.length > 0 && (
                            <Button
                              type={b.batch_no === selectedBatchNo ? "primary" : "text"}
                              size="small"
                              icon={<ArrowRightOutlined />}
                              onClick={() =>
                                setSelectedBatchNo((prev) =>
                                  prev === b.batch_no ? null : b.batch_no,
                                )
                              }
                            />
                          )}
                          {/* ลบหม้อเคี่ยวหม้อนี้ (แก้ไขได้เฉพาะโหมดแก้ไข) */}
                          {!isViewMode && (
                            <Popconfirm
                              title={`ลบ Batch ${b.batch_no}?`}
                              okText="ลบ"
                              cancelText="ยกเลิก"
                              onConfirm={() => {
                                handleBatchDelete(b);
                                setSelectedBatchNo((prev) =>
                                  prev === b.batch_no ? null : prev,
                                );
                              }}
                            >
                              <Button
                                type="text"
                                size="small"
                                danger
                                icon={<RiDeleteBin5Line />}
                              />
                            </Popconfirm>
                          )}
                        </Space>
                      </div>
                    ))}
                    <div style={{ padding: "6px 12px", borderTop: "0.5px solid var(--ant-color-border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      {(() => {
                        const target = Math.ceil(Number(sauceBatchWeightInput || 0));
                        const allBatchesTotal = Math.ceil(
                          (selectedRecord?.batches || []).reduce((sum, b) => sum + Number(b.qty || 0), 0)
                        );
                        const diff = target - allBatchesTotal;
                        if (!isViewMode && target > 0 && diff !== 0) {
                          return (
                            <Typography.Text type={diff > 0 ? "warning" : "danger"} style={{ fontSize: 12 }}>
                              {diff > 0
                                ? `ต้องใส่เพิ่มอีก ${formatMoney(diff, 0)} kg (รวมทุกวัน)`
                                : `เกินมา ${formatMoney(Math.abs(diff), 0)} kg (รวมทุกวัน)`}
                            </Typography.Text>
                          );
                        }
                        return <span />;
                      })()}
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        รวม {formatMoney(totalQty, 0)} kg
                      </Typography.Text>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Material panel */}
            {selectedBatchNo !== null && editableMaterials.length > 0 && (() => {
              const batch = (selectedRecord?.batches || []).find((b) => b.batch_no === selectedBatchNo);
              const mats = allBatchMaterials[selectedBatchNo] || [];
              return (
                <div
                  key={`mat-panel-${selectedBatchNo}-${batch?.qty ?? 0}`}
                  style={{ flex: 1, minWidth: 0, border: "0.5px solid var(--ant-color-border)", borderRadius: 6, overflow: "hidden" }}
                >
                  <div style={{ padding: "6px 12px", background: "var(--ant-color-fill-quaternary)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography.Text strong style={{ fontSize: 13 }}>
                      Material — Batch {selectedBatchNo}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {formatMoney(batch?.qty || 0, 2)} kg
                    </Typography.Text>
                  </div>
                  <Table
                    size="small"
                    rowKey="mat_code"
                    columns={batchMaterialColumns}
                    dataSource={mats}
                    pagination={false}
                  />
                </div>
              );
            })()}
          </div>
        </>
      ) : (
        <Empty description="ยังไม่ได้เลือก Job Order" />
      )}
    </Card>
  );

  const masterprocessContent = (
    <>
      <Table
        size="small"
        rowKey={(record) =>
          `${record?.stepno}-${record?.mpno}-${record?.stcode}`
        }
        columns={masterProcessColumns(productionQty, normalizedMasterProcessDetails)}
        dataSource={normalizedMasterProcessDetails}
        pagination={false}
        scroll={{ x: "max-content", y: 360 }}
      />
    </>
  );

  const mainInfoContent = (
    <Form form={form} layout="vertical" autoComplete="off">
      <Row gutter={[12, 12]}>
        <Col xs={24} md={6}>
          <Card>
            <Statistic
              title="JO Ordered"
              value={Number(jobOrder?.ordered_qty || 0)}
              formatter={(value) => formatMoney(Number(value || 0), 2)}
              suffix="Unit"
            />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic
              title="FG Stock"
              value={Number(summary?.fg_stock || 0)}
              formatter={(value) => formatMoney(Number(value || 0), 2)}
              suffix="Unit"
            />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic
              title="FG Stock After Produce"
              value={
                Number(summary?.fg_stock || 0) +
                Number(summary?.production_qty || 0)
              }
              formatter={(value) => formatMoney(Number(value || 0), 2)}
              suffix="Unit"
            />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic
              title="Against JO"
              value={Number(summary?.remaining_against_order || 0)}
              formatter={(value) => formatMoney(Number(value || 0), 2)}
              suffix="Unit"
              valueStyle={{
                color:
                  Number(summary?.remaining_against_order || 0) >= 0
                    ? "#15803d"
                    : "#b91c1c",
              }}
            />
          </Card>
        </Col>
      </Row>
      <Card>
        <Flex justify="space-between" align="center" wrap gap={8}>
          <Typography.Title level={4} className="m-0">
            FG ที่จะผลิต
          </Typography.Title>
          {selectedRecord && (
            <Tag color="blue">{selectedRecord.fg_code}</Tag>
          )}
        </Flex>
        <Divider />
        {selectedRecord ? (
          <>
          <Descriptions size="small" bordered column={{ xs: 1, sm: 2, md: 4 }}>
            {/* รหัส Production Order : มีเมื่อบันทึกแล้ว (แก้ไข / ดู) */}
            <Descriptions.Item label="PD Code">
              {config?.record?.pdcode ? (
                <Typography.Text strong copyable>
                  {config.record.pdcode}
                </Typography.Text>
              ) : (
                <Typography.Text type="secondary">ออกเลขอัตโนมัติเมื่อบันทึก</Typography.Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="FG Code">
              {selectedRecord.fg_code || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="FG Name">
              {selectedRecord.fg_name || "-"}
            </Descriptions.Item>
            
            <Descriptions.Item label="PD Date">
              {isViewMode ? (
                jobOrder?.pddate
                  ? dayjs(jobOrder.pddate).format("DD/MM/YYYY")
                  : form.getFieldValue("pd_date")?.format("DD/MM/YYYY") || "-"
              ) : (
                <Form.Item
                  name="pd_date"
                  rules={[{ required: true, message: "กรุณาระบุวันที่ Production Order" }]}
                  className="!mb-0"
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    style={{ width: "100%" }}
                    placeholder="เลือกวันที่ Production Order"
                    allowClear={false}
                  />
                </Form.Item>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="จำนวนผลิต (Unit)">
              {isViewMode ? (
                <span>{formatMoney(productionQty, 2)}</span>
              ) : (
                <InputNumber
                  min={0}
                  style={{ width: 160 }}
                  value={productionQty}
                  onChange={(value) => handleQtyChange(Number(value || 0))}
                />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="น้ำหนักซอสต่อครั้ง (kg)">
              {isViewMode ? (
                <span>{formatMoney(Number(sauceBatchWeightInput || 0), 2)}</span>
              ) : (
                <InputNumber
                  style={{ width: 160 }}
                  min={0}
                  precision={2}
                  step={0.01}
                  value={sauceBatchWeightInput}
                  onChange={(value) => {
                    if (!selectedRecord) return;
                    const nextWeight = Number(value || 0);
                    setSauceBatchWeightInput(nextWeight);
                    handleSauceBatchWeightChange(nextWeight);
                  }}
                />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="PG Code">
              {selectedRecord.pg_code || "-"}
            </Descriptions.Item>
          </Descriptions>
          </>
        ) : (
          <Empty description="ยังไม่ได้เลือก Job Order" />
        )}
      </Card>

      <Card>
        <Flex justify="space-between" align="center" wrap gap={8}>
          <Typography.Title level={4} className="m-0">
            Job Order อ้างอิง
          </Typography.Title>
          <Tag color="blue">{selectedRecords.length} รายการ</Tag>
        </Flex>
        <Divider />
        <Table
          size="small"
          rowKey="detail_code"
          columns={refColumns}
          dataSource={selectedRecords}
          pagination={false}
          scroll={{ x: "max-content" }}
        />
        {!isViewMode && (
          <Flex justify="flex-end" className="mt-3">
            <Button onClick={() => setOpenSelectorModal(true)}>
              + เพิ่ม Job Order
            </Button>
          </Flex>
        )}
      </Card>

      <Card>
        {!isViewMode && isPendingReprocess && (
          <Alert
            className="mb-3"
            type="warning"
            showIcon
            message="มีการเปลี่ยนข้อมูลการผลิต กรุณากดปุ่ม ประมวลผล เพื่อคำนวณ Material Check ใหม่"
          />
        )}
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
            <Descriptions.Item label="JO Code">
              {jobOrder?.jocode || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Customer">
              {jobOrder?.cusname || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Brand">
              {jobOrder?.brand || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="FG Code">
              {jobOrder?.fg_code || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="FG Name">
              {jobOrder?.fg_name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Net wt (gm)">
              {jobOrder?.net_weight ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label="น้ำหนักซอส">
              {sauceWeight || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="ส่วนสูญเสีย">
              {jobOrder?.lost || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="เตรียมน้ำหนักซอส">
              {preparedSauceWeight || "-"}
            </Descriptions.Item>
          </Descriptions>
      </Card>

      <Row gutter={[12, 12]}>
        <Col xs={24}>
          <Card title="Master Process ของ FG">
            {normalizedMasterProcessDetails.length > 0 ? (
              <>
                <Descriptions
                  size="small"
                  bordered
                  column={{ xs: 1, sm: 2, md: 4 }}
                  className="mb-3"
                >
                  <Descriptions.Item label="FG Code">
                    {jobOrder?.fg_code || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="FG Name">
                    {jobOrder?.fg_name || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Master Process Code">
                    {masterProcess?.header?.mpcode || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Remark">
                    {masterProcess?.header?.remark || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="จำนวน Batch">
                    {isViewMode ? (
                      selectedRecord?.batch_count || 1
                    ) : (
                      <InputNumber
                        size="small"
                        min={1}
                        style={{ width: 100 }}
                        value={selectedRecord?.batch_count || 1}
                        onChange={(value) =>
                          handleManualBatchCountChange(Number(value || 1))
                        }
                      />
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="น้ำหนักซอสต่อครั้ง">
                    {formatMoney(Number(sauceBatchWeightInput || 0), 2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="น้ำหนักซอสรวม">
                    {formatMoney(Number(totalSauceBatchWeight || 0), 2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="หน่วย">kg</Descriptions.Item>
                </Descriptions>

                <div
                  style={{
                    margin: "12px 0",
                    padding: "10px 14px",
                    background: "var(--ant-color-fill-quaternary)",
                    border: "1px solid var(--ant-color-border-secondary)",
                    borderRadius: 8,
                  }}
                >
                  <Flex align="center" gap={10} wrap>
                    <Typography.Text strong>
                      จำนวนผลิตต่อ batch (kg)
                    </Typography.Text>
                    {isViewMode ? (
                      <Typography.Text>
                        {formatMoney(Number(batchWeightDraft || 0), 2)}
                      </Typography.Text>
                    ) : (
                      <>
                        <InputNumber
                          size="small"
                          min={0}
                          style={{ width: 140 }}
                          value={batchWeightDraft}
                          onChange={(value) =>
                            setBatchWeightDraft(Number(value || 0))
                          }
                        />
                        <Button
                          size="small"
                          type="primary"
                          onClick={() => {
                            if (!selectedRecord) return;
                            handleProcessBatchPlan(Number(batchWeightDraft || 0));
                          }}
                        >
                          ประมวลผล
                        </Button>
                      </>
                    )}
                  </Flex>
                </div>

                {/* วันผลิต tabs */}
                <Divider orientation="left" className="my-3" style={{ fontSize: 13 }}>วันผลิต (ระบุจำนวน batch ของแต่ละวัน)</Divider>
                <Tabs
                  type="editable-card"
                  size="small"
                  hideAdd
                  activeKey={activePdDateTab}
                  onChange={setActivePdDateTab}
                  onEdit={(key, action) => { if (action === "remove") handleRemovePdDate(key); }}
                  tabBarExtraContent={
                    !isViewMode
                      ? {
                          left: (
                            <DatePicker
                              format="DD/MM/YYYY"
                              placeholder="+ เพิ่มวันผลิต"
                              value={null}
                              disabledDate={(d) => pdDates.some((pd) => pd.date === d.format("YYYY-MM-DD"))}
                              onChange={(d) => d && handleAddPdDate(d.format("YYYY-MM-DD"))}
                              style={{ width: 160, marginRight: 8 }}
                              className="bn-primary-outline"
                            />
                          ),
                        }
                      : undefined
                  }
                  items={pdDates.map((d) => ({
                    key: d.date,
                    /* โชว์จำนวน batch ต่อท้ายวันที่บนแท็บเลย เช่น 25/07/2026 (3 batch) */
                    label: `${dayjs(d.date).format("DD/MM/YYYY")} (${d.batch_count || 0} batch)`,
                    closable: !isViewMode,
                    children: (
                      <Flex align="center" gap={8} style={{ padding: "4px 0" }}>
                        {isViewMode ? null : (
                          <InputNumber
                            size="small"
                            min={1}
                            value={d.batch_count}
                            addonAfter="batch"
                            style={{ width: 120 }}
                            onChange={(v) => handleUpdatePdDateCount(d.date, v)}
                          />
                        )}
                        {pdDatesTotalBatch !== (selectedRecord?.batch_count || 0) && (
                          <Typography.Text type="warning" style={{ fontSize: 12 }}>
                            รวม {pdDatesTotalBatch}/{selectedRecord?.batch_count || 0} batch
                          </Typography.Text>
                        )}
                      </Flex>
                    ),
                  }))}
                />

                <Divider className="my-3" />
                <Tabs
                  defaultActiveKey="batch"
                  items={[
                    {
                      key: "batch",
                      label: activePdDateTab
                        ? `หม้อเคี่ยว ${dayjs(activePdDateTab).format("DD/MM/YY")}`
                        : "หม้อเคี่ยว",
                      children: (
                        <Space
                          direction="vertical"
                          size="middle"
                          style={{ display: "flex" }}
                        >
                          {sauceBatchContent}
                        </Space>
                      ),
                    },
                    {
                      key: "main2",
                      label: activePdDateTab
                        ? `Material ที่ใช้วันที่ ${dayjs(activePdDateTab).format("DD/MM/YY")}`
                        : "Material ที่ใช้ทั้งหมด",
                      children: (
                        <Table
                          size="small"
                          rowKey="mat_code"
                          columns={totalMaterialColumns(
                            !isViewMode && !activePdDateTab
                              ? handleMaterialQtyChange
                              : null,
                          )}
                          dataSource={dayMaterials}
                          pagination={false}
                          scroll={{ x: "max-content" }}
                          locale={{ emptyText: "กดประมวลผลเพื่อคำนวณ" }}
                        />
                      ),
                    },
                  ]}
                />
              </>
            ) : (
              <Empty description="FG นี้ยังไม่มี Master Process" />
            )}
          </Card>
        </Col>
      </Row>

    </Form>
  );

  const materialCheckContent = previewData ? (
    <Card>
      <Flex justify="space-between" align="center" wrap gap={8}>
        <Typography.Title level={4} className="m-0">
          Material Check
        </Typography.Title>
        <Space>
          <Tag color={summary?.can_cover_materials ? "success" : "error"}>
            {summary?.can_cover_materials
              ? "วัตถุดิบเพียงพอ"
              : "วัตถุดิบไม่เพียงพอ"}
          </Tag>
          <Tag color={summary?.can_cover_packaging ? "success" : "error"}>
            {summary?.can_cover_packaging
              ? "Packing เพียงพอ"
              : "Packing ไม่เพียงพอ"}
          </Tag>
        </Space>
      </Flex>
      <Divider />
      <Table
        size="small"
        rowKey="mat_code"
        columns={requirementColumns}
        dataSource={editableMaterials}
        pagination={false}
        scroll={{ x: "max-content" }}
      />
      <Divider>Packaging / Packing</Divider>
      {allPackaging.length > 0 ? (
        <Table
          size="small"
          rowKey="mat_code"
          columns={requirementColumns}
          dataSource={allPackaging}
          pagination={false}
          scroll={{ x: "max-content" }}
        />
      ) : (
        <Empty description="Target นี้ไม่มีรายการ Packing/Packaging ให้ตรวจสอบ" />
      )}
    </Card>
  ) : (
    <Card>
      {loading ? (
        <Typography.Text>กำลังตรวจสอบข้อมูลการผลิต...</Typography.Text>
      ) : (
        <Empty description="ยังไม่มีผลการตรวจสอบ" />
      )}
    </Card>
  );
  const SectionTop = (
    <Row
      gutter={[{ xs: 32, sm: 32, md: 32, lg: 12, xl: 12 }, 8]}
      className="m-0"
    >
      <Col flex="none" className="p-0">
        <Flex gap={4} justify="start">
          <Button
            style={ACTION_BTN}
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              handleBack();
            }}
          >
            กลับ
          </Button>
        </Flex>
      </Col>
      <Col flex="auto" style={{ paddingInline: 0 }}>
        {/* ปุ่มขนาดเท่ากันทุกปุ่ม และขึ้นบรรทัดใหม่เองเมื่อจอแคบ */}
        <Flex gap={6} justify="end" wrap="wrap">
          {/* แยกปุ่มเป็น 2 กลุ่ม: พิมพ์เอกสาร (ขอบสี) | ดำเนินการ (ทึบ) */}
          {!!config?.record?.pdcode && (
            <Flex gap={6} align="center" wrap="wrap" style={PRINT_GROUP}>
              <Typography.Text type="secondary" style={GROUP_LABEL}>
                <PrinterOutlined /> พิมพ์
              </Typography.Text>
              {[
                { label: "ห้องสารส่วนผสม", url: "/pd-requisition-print/" },
                { label: "ห้องตัดแต่ง", url: "/pd-trim-room/" },
                { label: "ห้องเคี่ยว", url: "/pd-curing-room/" },
              ].map((b) => (
                <Button
                  key={b.url}
                  className="bn-center justify-center"
                  style={PRINT_BTN}
                  icon={<PrinterOutlined style={{ fontSize: "1rem" }} />}
                  onClick={() =>
                    window.open(`${b.url}${encodeURIComponent(config.record.pdcode)}`, "_blank")
                  }
                >
                  {b.label}
                </Button>
              ))}
            </Flex>
          )}
          {!!config?.record?.pdcode && (
            <Flex gap={6} align="center" wrap="wrap" style={ACTION_GROUP}>
              <Typography.Text type="secondary" style={GROUP_LABEL}>
                ดำเนินการ
              </Typography.Text>
              <Button
                className="bn-center justify-center"
                style={{ ...ACTION_BTN, borderColor: "#389e0d", color: "#fff", background: "#389e0d" }}
                icon={<FileTextOutlined style={{ fontSize: "1rem" }} />}
                onClick={() =>
                  navigate("/ERP/requisition/manage/create", {
                    state: { config: { action: "create", title: "สร้างใบเบิกวัตถุดิบ", pdcode: config.record.pdcode } },
                  })
                }
              >
                สร้างใบเบิก
              </Button>
              <Button
                className="bn-center justify-center"
                style={{ ...ACTION_BTN, borderColor: "#0958d9", color: "#fff", background: "#0958d9" }}
                icon={<InboxOutlined style={{ fontSize: "1rem" }} />}
                onClick={() =>
                  navigate("/ERP/fg-handover/manage/create", {
                    state: { config: { action: "create", title: "สร้างใบส่งมอบ FG", pdcode: config.record.pdcode } },
                  })
                }
              >
                ส่งมอบ FG
              </Button>
            </Flex>
          )}
          {/* {formDetail.doc_status === "ยังไม่ได้รับของ" &&
            config?.action !== "create" && (
              <Popconfirm
                placement="topRight"
                title="ยืนยันการยกเลิก"
                description="คุณแน่ใจที่จะยกเลิกJO?"
                icon={<QuestionCircleOutlined style={{ color: "red" }} />}
                onConfirm={() => handleCancel()}
              >
                <Button
                  className="bn-center justify-center"
                  icon={<CloseCircleFilledIcon style={{ fontSize: "1rem" }} />}
                  type="primary"
                  style={ACTION_BTN}
                  danger
                >
                  ยกเลิกJO
                </Button>
              </Popconfirm>
            )} */}
          {!selectedRecords.length || isViewMode ? (
            ""
          ) : (
            <Button
              className="bn-center justify-center"
              icon={<SaveFilled style={{ fontSize: "1rem" }} />}
              type="primary"
              style={ACTION_BTN}
              disabled={!previewData || isPendingReprocess}
              onClick={() => {
                handleConfirm();
              }}
            >
              Save
            </Button>
          )}
        </Flex>
      </Col>
    </Row>
  );

  if (editLoading) {
    return (
      <div className="productionorder-manage xs:px-0 sm:px-0 md:px-8 lg:px-8">
        <Flex justify="center" align="center" style={{ minHeight: 300 }}>
          <Typography.Text type="secondary">กำลังโหลดข้อมูล Production Order...</Typography.Text>
        </Flex>
      </div>
    );
  }

  return (
    <div className="productionorder-manage xs:px-0 sm:px-0 md:px-8 lg:px-8">
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        {SectionTop}
        <Flex justify="space-between" align="center" gap={8} wrap>
          <Typography.Title level={3} className="m-0 !text-zinc-800">
            Production Order
          </Typography.Title>
        </Flex>

        {!selectedRecords.length ? (
          <Card>
            <Empty description="ยังไม่ได้เลือก Job Order สำหรับสร้าง Production Order" />
            {config?.action === "create" && (
              <Flex justify="center" className="mt-3">
                <Button type="primary" onClick={() => setOpenSelectorModal(true)}>
                  เลือก Job Order
                </Button>
              </Flex>
            )}
          </Card>
        ) : (
          <>
            <Tabs
              defaultActiveKey="main"
              items={[
                {
                  key: "main",
                  label: "ข้อมูลหลัก",
                  children: (
                    <Space
                      direction="vertical"
                      size="middle"
                      style={{ display: "flex" }}
                    >
                      {mainInfoContent}
                    </Space>
                  ),
                },
                /* แท็บหม้อเคี่ยวถูกเอาออก — ดูหม้อเคี่ยวได้ในแท็บวันผลิตของข้อมูลหลักแทน */
                {
                  key: "material-check",
                  label: "Material Check",
                  children: materialCheckContent,
                },
                {
                  key: "process-steps",
                  label: "ขั้นตอนการผลิต",
                  children: (
                    <ProcessStepsPanel pdcode={config?.record?.pdcode} />
                  ),
                },
                // {
                //   key: "lot-allocation",
                //   label: "Lot Allocation",
                //   children: lotAllocationContent,
                // },
              ]}
            />
          </>
        )}

        <ModalProductOrderSelector
          open={openSelectorModal}
          close={() => setOpenSelectorModal(false)}
          onSelect={handleSelectRecord}
          selectedRecords={selectedRecords}
          selectedProduceType={produceType}
          lockedFgCode={selectedRecords[0]?.fg_code || null}
        />
      </Space>
    </div>
  );
}

export default ProductionOrderManage;
